import { ReactComponent as CopyIcon } from "@/assets/icons/copy.svg";
import { ReactComponent as EditIcon } from "@/assets/icons/edit-2.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trash.svg";
import { Toggle, Typography, Icon, Tooltip } from "@/components/atoms";
import { DataTableColumnDef } from "@/components/molecules";
import { VehicleType } from "@/redux/slices/vehicle/types";
import { formatCurrency } from "@/utils";
import { VehicleTypesEditorTableData } from "./types";

export const generateColumns = (data: VehicleTypesEditorTableData[]) => {
  const columns: DataTableColumnDef<VehicleTypesEditorTableData>[] = [
    {
      accessorKey: "reorder",
      header: "",
      size: 50,
      cell: (props) => props.row.original.reorder,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: `Name (${data.length})`,
      size: 245,
      cell: (props) => props.row.original.name,
      enableSorting: false,
    },
    { accessorKey: "passenger", header: "Capacity", enableSorting: false, size: 90 },
    { accessorKey: "bags", header: "No. of Bags", enableSorting: false, size: 110 },
    { accessorKey: "costAdjustment", header: "Cost Adjustment", enableSorting: false, size: 150 },
    { accessorKey: "waitTimeCost", header: "Waiting Time", enableSorting: false, size: 135 },
    {
      accessorKey: "mappedCategory",
      header: () => (
        <span className="flex items-center gap-1.5">
          Mapped to (Vehicle Category)
          <Tooltip
            content="The system category that determines which vehicles can dispatch for this type."
            placement="top"
          >
            <span className="inline-flex">
              <Icon name="InfoCircle" size="sm" className="text-neutral-silver-gray" />
            </span>
          </Tooltip>
        </span>
      ),
      cell: (props) => props.row.original.mappedCategory,
      enableSorting: false,
      size: 260,
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (props) => props.row.original.actions,
      size: 140,
      enableSorting: false,
    },
  ];
  return columns;
};

type Handlers = {
  onToggle: (id: string, active: boolean) => void;
  onEdit: (item: VehicleType) => void;
  onDelete: (item: VehicleType) => void;
  onCopy: (item: VehicleType) => void;
};

export const generateTableData = (items: VehicleType[], h: Handlers): VehicleTypesEditorTableData[] => {
  return items.map((i) => {
    const missingCategory = !i.vehicleCategory;
    const nameCell = (
      <div className="flex items-center gap-2">
        <div className="flex flex-col">
          <Typography className="leading-6">{i.name}</Typography>
          {i.isDefault && (
            <span className="flex items-center gap-1">
              <Typography className="text-sm font-medium leading-4 text-neutral-silver-gray">System Default</Typography>
              <Tooltip
                content="This is a system default, so it can't be deleted. You can disable, reorder, or duplicate it instead"
                placement="top"
              >
                <span className="inline-flex">
                  <Icon name="InfoCircle" size="sm" className="text-neutral-silver-gray" />
                </span>
              </Tooltip>
            </span>
          )}
        </div>
        {missingCategory && (
          <Tooltip
            content={`Map "${i.name}" to a vehicle category so jobs offloaded outside your fleet can be dispatched in the network.`}
            placement="top"
          >
            <span className="inline-flex">
              <Icon name="Danger" variant="Bold" size="md" className="text-warning" />
            </span>
          </Tooltip>
        )}
      </div>
    );

    const mappedCategoryCell = (
      <>
        {missingCategory && (
          <span aria-hidden className="pointer-events-none absolute inset-0 bg-warning-light" />
        )}
        <Typography className="relative">{i.vehicleCategory?.name ?? "-"}</Typography>
      </>
    );

    const actionsCell = i.isDefault ? (
      <span className="flex items-center gap-1.5">
        <Toggle size="xs" checked={i.isActive} onChange={(checked) => h.onToggle(i.uuid, checked)} className="mr-1" />
        <Tooltip content="Duplicate Vehicle Type" placement="top">
          <button
            type="button"
            onClick={() => h.onCopy(i)}
            aria-label="Duplicate vehicle type"
            className="flex size-8 items-center justify-center rounded-md p-1 text-neutral-dark-gray hover:bg-neutral-gray"
          >
            <CopyIcon className="size-6" />
          </button>
        </Tooltip>
      </span>
    ) : (
      <span className="flex items-center gap-1.5">
        <Toggle size="xs" checked={i.isActive} onChange={(checked) => h.onToggle(i.uuid, checked)} className="mr-1" />
        <button
          type="button"
          onClick={() => h.onEdit(i)}
          aria-label="Edit vehicle type"
          className="flex size-8 items-center justify-center rounded-md p-1 text-neutral-dark-gray hover:bg-neutral-gray"
        >
          <EditIcon className="size-6" />
        </button>
        <button
          type="button"
          onClick={() => h.onDelete(i)}
          aria-label="Delete vehicle type"
          className="flex size-8 items-center justify-center rounded-md p-1 text-neutral-dark-gray hover:bg-neutral-gray"
        >
          <TrashIcon className="size-6" />
        </button>
      </span>
    );

    return {
      id: i.uuid,
      isDefault: i.isDefault,
      reorder: <></>,
      name: nameCell,
      passenger: `${i.vehicleCapacity.pax} pax`,
      bags: i.vehicleCapacity.bags,
      costAdjustment: `${i.perDistanceUnitMultiplier > 0 ? "+" : ""}${i.perDistanceUnitMultiplier}%`,
      waitTimeCost: `${formatCurrency(i.waitTimeCost, 2, i.metadata.currency)} /minute`,
      mappedCategory: mappedCategoryCell,
      actions: actionsCell,
    };
  });
};
